<template>
  <Dialog
    title="Add Product"
    :showCloseButton="true"
    :showFooter="true"
    :showSaveButton="false"
    :showCancelButton="false"
    :custom-footer="true"
    :md="true"
    @closeEvent="closeEvent"
  >
    <template #modal-content>
      <form id="form-add-product" @submit.prevent="onSubmit">
        <Switch
          @update:modelValue="(e) => (isCustomProduct = e)"
          :checked="isCustomProduct"
          id="custom_product"
          type="checkbox"
          title="Custom Product"
          name="product"
        />
        <template v-if="isCustomProduct">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block mb-1 text-sm font-medium text-gray-700"> Product Name </label>
              <div>
                <input
                  type="text"
                  placeholder="Add Product Name"
                  v-model="formData.customProductName"
                  class="w-full px-2 py-[9px] border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  :class="{ 'border-red-500': errors.customProductName }"
                />
                <span v-if="errors.customProductName" class="text-xs text-red-500">
                  {{ errors.customProductName }}
                </span>
              </div>
            </div>
            <div>
              <label class="block mb-1 text-sm font-medium text-gray-700">
                Select Factory
              </label>
              <div>
                <Select
                  append-to-body
                  label="name"
                  track-by="id"
                  :clearable="false"
                  v-model="formData.selectedFactoryId"
                  :options="factories"
                  :reduce="(factory) => factory.id"
                  @search="onSearch"
                  placeholder="Please select factory"
                  @input="onInput"
                  :loading="loading"
                />
              </div>
            </div>

            <div>
              <label class="block mb-1 text-sm font-medium text-gray-700"> Design name </label>
              <div>
                <input
                  type="text"
                  placeholder="Add Design name"
                  v-model="formData.customDesignName"
                  class="w-full px-2 py-[9px] border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  :class="{ 'border-red-500': errors.customDesignName }"
                />
                <span v-if="errors.customDesignName" class="text-xs text-red-500">
                  {{ errors.customDesignName }}
                </span>
              </div>
            </div>
          </div>

          <div
            class="relative flex flex-col w-full p-2 py-4 mt-5 border border-gray-300 rounded-md"
          >
            <h3
              class="text-lg font-light text-gray-400 absolute translate-y-[-100%] left-4 bg-white"
            >
              Upload Files
            </h3>

            <!-- Assets Upload -->
            <div class="flex flex-col w-full gap-2">
              <div>
                <input
                  type="file"
                  class="hidden"
                  multiple="true"
                  id="assets"
                  @change="(e) => fileChange(e, 'assets')"
                />
                <label
                  for="assets"
                  class="flex w-full gap-4 px-2 py-2 border rounded-md cursor-pointer input"
                  :class="{ 'border-red-500': errors.productAssets }"
                >
                  <ArrowUpTrayIcon class="size-6" />
                  Upload Assets
                </label>
                <span v-if="errors.productAssets" class="text-xs text-red-500">
                  {{ errors.productAssets }}
                </span>
              </div>
              <!-- Asset previews -->
              <div class="flex flex-wrap items-center gap-2">
                <div
                  v-for="(item, index) in formData.productAssets"
                  :key="index"
                  class="relative w-[100px] h-[100px] border border-gray-300 rounded-md overflow-hidden"
                >
                  <button
                    type="button"
                    class="absolute p-1 text-white bg-red-500 rounded-full top-1 left-1"
                    @click="removeFile(index, 'assets')"
                  >
                    <TrashIcon class="w-4 h-4" />
                  </button>
                  <img
                    :src="getFilePreview(item)"
                    class="object-cover w-full h-full"
                    alt="Preview"
                  />
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-if="!isCustomProduct">
          <div class="grid grid-cols-2 gap-4">
            <!-- Product Selection -->
            <div>
              <label class="block mb-1 text-sm font-medium text-gray-700">
                Select Product/SKU
              </label>
              <div>
                <Select
                  append-to-body
                  label="display_name"
                  track-by="id"
                  :clearable="false"
                  v-model="formData.selectedProduct"
                  :options="company_products"
                  :reduce="(product: Product) => product"
                  @option:selecting="onOptionSelecting"
                  @search="onSearch"
                  placeholder="Please select product"
                  @input="onInput"
                />
                <span v-if="errors.selectedProduct" class="text-xs text-red-500">
                  {{ errors.selectedProduct }}
                </span>
              </div>
            </div>
             <div>
              <label class="block mb-1 text-sm font-medium text-gray-700">
                Select Factory
              </label>
              <div>
                <Select
                  append-to-body
                  label="name"
                  track-by="id"
                  :clearable="false"
                  v-model="formData.selectedFactoryId"
                  :options="factories"
                  :reduce="(factory) => factory.id"
                  @search="onSearch"
                  placeholder="Please select factory"
                  @input="onInput"
                  :loading="loading"
                />
              </div>
            </div>

            <!-- Style Selection -->
            <div>
              <label class="block mb-1 text-sm font-medium text-gray-700">Select Style</label>
              <Select
                append-to-body
                label="name"
                track-by="id"
                placeholder="Select style"
                :options="formData.selectedProduct?.productstyles"
                :clearable="false"
                v-model="formData.selectedStyle"
                :disabled="!formData.selectedProduct"
                :class="{ 'border-red-500': errors.selectedStyle }"
                @option:selected="() => resetProductFields('selectedStyle')"
              />
              <span v-if="errors.selectedStyle" class="text-xs text-red-500">
                {{ errors.selectedStyle }}
              </span>
            </div>

            <!-- Design Selection -->
            <div>
              <label class="block mb-1 text-sm font-medium text-gray-700">Select Design</label>
              <Select
                append-to-body
                label="design_name"
                track-by="id"
                placeholder="Select design"
                :searchable="false"
                :filterable="false"
                :options="formData.selectedStyle?.productdesigns || [] "
                :clearable="false"
                v-model="formData.selectedDesign"
                :disabled="!formData.selectedStyle"
                :class="{ 'border-red-500': errors.selectedDesign }"
              />
              <span v-if="errors.selectedDesign" class="text-xs text-red-500">
                {{ errors.selectedDesign }}
              </span>
            </div>
          </div>
        </template>
        <div></div>
        <div class="flex gap-4 mt-4">
          <!-- Front Image Upload -->
          <div class="flex flex-col w-full gap-2">
            <div>
              <input
                type="file"
                class="hidden"
                id="frontimage"
                accept="image/*"
                @change="(e) => fileChange(e, 'frontimage')"
              />
              <label
                for="frontimage"
                class="flex w-full gap-4 px-2 py-2 border rounded-md cursor-pointer input"
                :class="{ 'border-red-500': errors.frontImage }"
              >
                <ArrowUpTrayIcon class="size-6" />
                Upload Front Image
              </label>
              <span v-if="errors.frontImage" class="text-xs text-red-500">
                {{ errors.frontImage }}
              </span>
            </div>
            <!-- Front image preview -->
            <template v-if="formData.frontImage">
              <div
                class="relative w-[100px] h-[100px] border border-gray-300 rounded-md overflow-hidden"
              >
                <button
                  type="button"
                  class="absolute p-1 text-white bg-red-500 rounded-full top-1 left-1"
                  @click="removeFile(null, 'frontimage')"
                >
                  <TrashIcon class="w-4 h-4" />
                </button>
                <img
                  :src="getFilePreview(formData.frontImage)"
                  class="object-cover w-full h-full"
                  alt="Preview"
                />
              </div>
            </template>
          </div>

          <!-- Back Image Upload -->
          <div class="flex flex-col w-full gap-2">
            <div>
              <input
                type="file"
                class="hidden"
                id="backimage"
                accept="image/*"
                @change="(e) => fileChange(e, 'backimage')"
              />
              <label
                for="backimage"
                class="flex w-full gap-4 px-2 py-2 border rounded-md cursor-pointer input"
                :class="{ 'border-red-500': errors.backImage }"
              >
                <ArrowUpTrayIcon class="size-6" />
                Upload Back Image
              </label>
              <span v-if="errors.backImage" class="text-xs text-red-500">
                {{ errors.backImage }}
              </span>
            </div>
            <!-- Back image preview -->
            <template v-if="formData.backImage">
              <div
                class="relative w-[100px] h-[100px] border border-gray-300 rounded-md overflow-hidden"
              >
                <button
                  type="button"
                  class="absolute p-1 text-white bg-red-500 rounded-full top-1 left-1"
                  @click="removeFile(null, 'backimage')"
                >
                  <TrashIcon class="w-4 h-4" />
                </button>
                <img
                  :src="getFilePreview(formData.backImage)"
                  class="object-cover w-full h-full"
                  alt="Preview"
                />
              </div>
            </template>
          </div>
        </div>

        <div
          class="flex flex-col w-full gap-3 mt-2"
          v-if="
            ((!checkIsEmpty(formData.selectedStyle?.customized_addons.grouped_addons) ||
              !checkIsEmpty(formData.selectedStyle?.customized_addons.ungrouped_addons)) &&
              formData.selectedStyle) ||
            (!checkIsEmpty(formData.selectedProduct?.active_addons) && formData.selectedProduct) ||
            (!checkIsEmpty(formData.selectedProduct?.logo_technologies) && formData.selectedProduct)
          "
        >
          <h4 class="text-lg font-semibold">Select Addons</h4>
          <template
            v-if="
              !checkIsEmpty(formData.selectedStyle?.customized_addons.grouped_addons) &&
              formData.selectedStyle
            "
          >
            <template
              v-for="(group, index) in Object.keys(
                formData.selectedStyle.customized_addons.grouped_addons
              )"
              :key="index"
            >
              <h2 class="font-semibold text-md">{{ group }}:</h2>
              <div class="flex items-center gap-2">
                <template
                  v-for="addon in formData.selectedStyle.customized_addons.grouped_addons[group]"
                  :key="addon.id"
                >
                  <label
                    :for="addon.id"
                    class="flex items-center gap-2 p-2 duration-200 bg-gray-200 rounded-md cursor-pointer select-none"
                  >
                    <input
                      type="radio"
                      :name="group"
                      :id="addon.id"
                      :checked="selectedGroupAddon[group]?.id === addon.id"
                      @change="setSelectedGroupedAddons(group, addon)"
                      class="hidden"
                    />
                    <label
                      :for="addon.id"
                      class="p-1 size-6 border-[1px] border-gray-400 rounded-md flex gap-1 items-center justify-center cursor-pointer bg-white"
                    >
                      <CheckIcon
                        class="!text-black checkIcon"
                        :class="{
                          hidden: selectedGroupAddon[group]?.id !== addon.id,
                        }"
                      />
                    </label>
                    {{ addon.sku_id }}
                  </label>
                </template>
              </div>
            </template>
            <hr class="border-gray-300" />
          </template>
          <template
            v-if="
              !checkIsEmpty(formData.selectedStyle?.customized_addons.ungrouped_addons) &&
              formData.selectedStyle
            "
          >
            <div class="flex items-center gap-2">
              <template
                v-for="addon in formData.selectedStyle.customized_addons.ungrouped_addons"
                :key="addon.id"
              >
                <label
                  :for="addon.id?.toString()"
                  class="flex items-center gap-2 p-2 duration-200 bg-gray-200 rounded-md cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    :id="addon.id?.toString()"
                    v-model="addon.is_selected"
                    @change="
                      (e) => {
                        addon.is_selected = (e?.target as HTMLInputElement)?.checked;
                      }
                    "
                    class="hidden"
                  />
                  <label
                    :for="addon.id?.toString()"
                    class="p-1 size-6 border-[1px] border-gray-400 rounded-md flex gap-1 items-center justify-center cursor-pointer bg-white"
                  >
                    <CheckIcon class="!text-black checkIcon hidden" />
                  </label>
                  {{ addon.sku_id }}
                </label>
              </template>
            </div>
            <hr class="border-gray-300" />
          </template>
          <template
            v-if="
              !checkIsEmpty(formData.selectedProduct?.active_addons) && formData.selectedProduct
            "
          >
            <div class="flex items-center gap-2">
              <template
                v-for="(addon, addonIndex) in formData.selectedProduct.active_addons"
                :key="addon.id"
              >
                <label
                  :for="addon.id?.toString()"
                  class="flex items-center gap-2 p-2 duration-200 bg-gray-200 rounded-md cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    :id="addon.id?.toString()"
                    class="hidden"
                    @change="(e) => setSelectedAddons(e, addon, addonIndex)"
                  />
                  <label
                    :for="addon.id?.toString()"
                    class="p-1 size-6 border-[1px] border-gray-400 rounded-md flex gap-1 items-center justify-center cursor-pointer bg-white"
                  >
                    <CheckIcon class="!text-black checkIcon hidden" />
                  </label>
                  {{ addon.title }}
                </label>
              </template>
            </div>
            <hr class="border-gray-300" />
          </template>
          <template
            v-if="
              !checkIsEmpty(formData.selectedProduct?.logo_technologies) && formData.selectedProduct
            "
          >
            <div class="flex items-center gap-2">
              <template
                v-for="(logotechnology, logotechnologyIndex) in formData.selectedProduct
                  .logo_technologies"
                :key="logotechnology.sku_id"
              >
                <label
                  :for="logotechnology.sku_id?.toString()"
                  class="flex items-center gap-2 p-2 duration-200 bg-gray-200 rounded-md cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    :id="logotechnology.sku_id?.toString()"
                    class="hidden"
                    @change="(e) => setLogoTechnologies(e, logotechnology, logotechnologyIndex)"
                  />
                  <label
                    :for="logotechnology.sku_id?.toString()"
                    class="p-1 size-6 border-[1px] border-gray-400 rounded-md flex gap-1 items-center justify-center cursor-pointer bg-white"
                  >
                    <CheckIcon class="!text-black checkIcon hidden" />
                  </label>
                  {{ logotechnology.label }}
                </label>
              </template>
            </div>
            <hr class="border-gray-300" />
          </template>
        </div>
        <div class="w-full py-4 mx-auto">
          <!-- Header Section -->
          <div class="mb-6">
            <p class="mb-3 text-gray-700">Add order details from excel file:</p>
            <div class="flex flex-wrap gap-3 mb-6">
              <button
                type="button"
                :disabled="!formData.selectedProduct"
                :title="formData.selectedProduct ? '' : 'No Product Selected'"
                class="inline-flex items-center px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition-colors disabled:opacity-[0.7] disabled:cursor-not-allowed"
                @click="
                  formData.selectedProduct
                    ? downloadTemplate(
                        formData.selectedProduct.id,
                        formData.selectedProduct.display_name
                      )
                    : false
                "
              >
                <ArrowDownTrayIcon class="w-4 h-4 mr-2" />
                Download roster excel template
              </button>
              <input
                type="file"
                id="rosterinput"
                @change="(e: Event) => fileChange(e, 'roster')"
                class="hidden"
              />
              <label
                type="button"
                class="inline-flex items-center px-4 py-2 transition-colors border rounded bg-primary-light text-primary border-primary hover:bg-primary-lighter"
                for="rosterinput"
              >
                <ArrowUpTrayIcon class="w-4 h-4 mr-2" />
                Upload roster excel template
              </label>
            </div>

            <div class="flex flex-wrap items-center gap-3">
              <div class="w-full sm:w-64">
                <Select
                  placeholder="Select Locker"
                  v-model="selectedLocker"
                  :options="lockerRoom?.filter((room) => room.id)"
                  track-by="id"
                  append-to-body
                  label="room_name"
                />
              </div>
              <div class="w-full sm:w-64">
                <Select
                  placeholder="Select Locker Product"
                  v-model="selectedLockerProduct"
                  :options="selectedLocker?.products || []"
                  track-by="id"
                  label="product_name"
                  @option:selected="setRosterData"
                  append-to-body
                />
              </div>
            </div>
          </div>

          <!-- Players Section -->
          <div class="w-full py-4 mx-auto">
            <table class="w-full mb-4">
              <thead>
                <tr class="text-left">
                  <th class="pb-2">Name</th>
                  <th class="pb-2">No</th>
                  <th class="pb-2">Size</th>
                  <th class="pb-2">Qty</th>
                  <th class="pb-2"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(player, index) in formData.players"
                  :key="`player-${index}`"
                  class="border-t"
                >
                  <td class="py-2">
                    <div>
                      <input
                        v-model="player.text"
                        type="text"
                        class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        :class="{
                          'border-red-500': getPlayerError(index, 'text'),
                        }"
                        :title="getPlayerError(index, 'text')"
                      />
                    </div>
                  </td>
                  <td class="py-2">
                    <input
                      v-model="player.number"
                      type="text"
                      class="w-20 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      :class="{
                          'border-red-500': getPlayerError(index, 'number'),
                        }"
                        :title="getPlayerError(index, 'number')"
                    />
                  </td>
                  <td class="py-2">
                    <div v-if="isCustomProduct">
                      <input
                        v-model="player.size"
                        type="text"
                        class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        :class="{
                          'border-red-500': getPlayerError(index, 'size'),
                        }"
                        :title="getPlayerError(index, 'size')"
                      />
                    </div>
                    <div v-else>
                      <div class="flex w-100">
                        <ExclamationCircleIcon
                          v-if="getPlayerError(index, 'size')"
                          class="text-red-500"
                          style="font-size: 2px; height: 25px; width: 25px"
                        />
                        <Select
                          v-model="player.size"
                          :options="product_sizes"
                          style="width: 100%"
                          :clearable="false"
                          append-to-body
                          placement="auto"
                          :title="getPlayerError(index, 'size')"
                        />
                      </div>
                    </div>
                  </td>
                  <td class="py-2">
                    <div>
                      <input
                        v-model.number="player.quantity"
                        min="1"
                        type="number"
                        class="w-20 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        :class="{
                          'border-red-500': getPlayerError(index, 'quantity'),
                        }"
                        :title="getPlayerError(index, 'quantity')"
                      />
                    </div>
                  </td>
                  <td class="py-2" v-if="index > 0">
                    <button
                      type="button"
                      @click="() => removePlayer(index)"
                      class="p-2 text-red-500 rounded-full hover:bg-red-50"
                    >
                      <XMarkIcon class="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <button
              type="button"
              @click="addPlayer"
              class="px-4 py-2 text-white transition-colors rounded bg-primary hover:bg-primary-dark"
            >
              Add Player
            </button>
            <button v-if="((isCustomProduct && formData.customProductName) || formData.selectedProduct) && formData.players.length > 0"
              type="button"
              @click="copyRoster(formData.players)"
              class="px-4 ml-2 py-2 text-white transition-colors rounded bg-primary hover:bg-primary-dark"
            >
              Copy Roster
            </button>
            <button v-if="((isCustomProduct && formData.customProductName) || formData.selectedProduct) && copiedRoster.isAnyRosterCopied && copiedRoster.copiedFromProductIndex !== formData.selectedProduct?.id"
              type="button"
              @click="pasteRoster()"
              class="px-4 ml-2 py-2 text-white transition-colors rounded bg-primary hover:bg-primary-dark"
            >
              Paste Roster
            </button>
          </div>
        </div>
      </form>
    </template>

    <template #footer>
      <div class="flex justify-end gap-x-3">
        <button
          type="button"
          @click="closeEvent"
          class="px-4 py-2 bg-white text-[#219F84] rounded-md hover:bg-[#219F84] hover:text-white border border-[#219F84] transition-colors"
        >
          Close
        </button>
        <button
          type="submit"
          form="form-add-product"
          class="px-4 py-2 bg-[#219F84] text-white rounded-md"
          :disabled="isSubmitting"
        >
          Add Product
        </button>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import placeholderImg from '@/assets/img/placeholder.png';
import Dialog from '@/components/Shared/Dialog.vue';
import Select from '@/components/Shared/Select.vue';
import Switch from '@/components/Shared/Switch.vue';
import { checkIsEmpty, downloadTemplate, logError } from '@/composables';
import { showAlert, showToast } from '@/composables/swal';
import { handleResponseException } from '@/interceptors/axiosRequestInterceptor';
import {
  DashboardItem,
  GroupedAddons,
  LockerRoom,
  LockerRoomProduct,
  Product,
  Productdesign,
  Productstyle,
} from '@/models';
import { CopiedRoster } from '@/models/CopiedRoster';
import { useMainStore } from '@/store';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ExclamationCircleIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import { CheckIcon } from '@heroicons/vue/24/solid';
import { storeToRefs } from 'pinia';
import readXlsxFile from 'read-excel-file';
import { computed, ComputedRef, defineEmits, onMounted, reactive, ref, watch } from 'vue';
import * as yup from 'yup';

interface Player {
  text: string;
  number: string;
  size: string;
  quantity: number;
  information: string;
}

const props = defineProps<{
  customerId: number;
  companyId: number;
  orderItem: any;
  lockerRoom: any,
  factories: any,
  company_products: any
  copiedRoster: CopiedRoster

}>();

// Form data state
const formData = reactive({
  selectedProduct: null as Product | null,
  selectedStyle: null as Productstyle | null,
  selectedDesign: null as Productdesign | null,
  customProductName: null as string | null,
  customDesignName: null as string | null,
  productAssets: [] as File[],
  frontImage: undefined as File | undefined,
  backImage: undefined as File | undefined,
  selectedFactoryId: null as any | null,
  players: [
    {
      text: '',
      number: '',
      size: '',
      quantity: 1,
      information: '',
    },
  ] as Player[],
});

// Form errors state
const errors = reactive({} as Record<string, string>);
const playerErrors = reactive([] as Array<Record<string, string>>);

// Other reactive state
const isCustomProduct = ref<boolean>(false);
const isSubmitting = ref<boolean>(false);
const selectedAddons = ref<any[]>([]);
const selectedTechnology = ref<any[]>([]);
const selectedGroupAddon = ref<GroupedAddons>({});
const selectedLocker = ref<LockerRoom | null>(null);
const selectedLockerProduct = ref<LockerRoomProduct | null>();
const rosterFile = ref<File>();
const searchActive = ref(false);

// Store references
const emit = defineEmits(['onClose', 'add-factory-product','copy-roster']);
const { loading } = storeToRefs(useMainStore());

// Computed properties
const product_sizes: ComputedRef<string[]> = computed(() => {
  let product_sizes: string[] = [];
  if (formData.selectedProduct) {
    product_sizes = formData.selectedProduct.sizes[0].json_data.map((size) => size.name);
  }
  return product_sizes;
});

const playersSchema = yup
  .array()
  .of(
    yup.object({
      text: yup.string(),
      // Number is optional; when provided it must be numeric (no decimals/special chars)
      number: yup
        .string()
        .nullable()
        .test(
          'valid-number-format',
          'Number must contain only digits (0-9)',
          (value) => !value || /^[0-9]+$/.test(value)
        ),
      size: yup
        .mixed()
        .required('Size can not be empty')
        .test('size-not-empty', 'Size can not be empty', function (value) {
          // Handle null or undefined
          if (value === null || value === undefined) {
            return false;
          }

          // Handle string values (for custom products)
          if (typeof value === 'string') {
            return value.trim() !== '';
          }

          // Handle object values (for standard products with Select component)
          if (typeof value === 'object') {
            // If it's an object, it should be truthy and not empty
            return value !== null && Object.keys(value).length > 0;
          }

          // For any other type, consider it valid if it's truthy
          return Boolean(value);
        }),
      quantity: yup.number().default(1).min(1, 'Quantity must be greater than 0'),
      information: yup.string(),
    })
  )
  .min(1, 'At least one player is required');
const customProductSchema = yup.object({
  customProductName: yup.string().required('Product name is required'),
  customDesignName: yup.string().required('Design name is required'),
  productAssets: yup
    .array()
    .min(1, 'At least one asset is required')
    .required('Assets are required'),
  players: playersSchema,
});

const standardProductSchema = yup.object({
  selectedProduct: yup.object().nullable().required('Product selection is required'),
  selectedStyle: yup
    .object()
    .nullable()
    .when('selectedProduct', {
      is: (val: any) => !!val,
      then: (schema) => schema.required('Style selection is required'),
    }),
  selectedDesign: yup
    .object()
    .nullable()
    .when('selectedStyle', {
      is: (val: any) => !!val,
      then: (schema) => schema.required('Design selection is required'),
    }),
  players: playersSchema,
});

// Enhanced validation function with better handling for mixed types
const validateForm = async (): Promise<boolean> => {
  try {
    // Clear previous errors
    Object.keys(errors).forEach((key) => delete errors[key]);
    playerErrors.splice(0, playerErrors.length);

    // Select schema based on product type
    const schema = isCustomProduct.value ? customProductSchema : standardProductSchema;

    // Additional manual validation for players to ensure we catch all empty size cases
    formData.players.forEach((player, index) => {
      if (!playerErrors[index]) {
        playerErrors[index] = {};
      }

      // Enhanced size validation for both string and object types
      let sizeIsEmpty = false;

      if (player.size === null || player.size === undefined) {
        sizeIsEmpty = true;
      } else if (typeof player.size === 'string') {
        sizeIsEmpty = player.size.trim() === '';
      } else if (typeof player.size === 'object') {
        sizeIsEmpty = Object.keys(player.size).length === 0;
      } else {
        sizeIsEmpty = !Boolean(player.size);
      }

      if (sizeIsEmpty) {
        playerErrors[index]['size'] = 'Size can not be empty';
      }

      // Check quantity
      if (!player.quantity || player.quantity < 1) {
        playerErrors[index]['quantity'] = 'Quantity must be greater than 0';
      }
    });

    // Check if we have any player errors
    const hasPlayerErrors = playerErrors.some((playerError) => Object.keys(playerError).length > 0);

    if (hasPlayerErrors) {
      return false;
    }

    // Validate the form data with yup
    await schema.validate(formData, { abortEarly: false });
    return true;
  } catch (validationError) {
    if (validationError instanceof yup.ValidationError) {
      // Process validation errors
      validationError.inner.forEach((error) => {
        const path = error.path || '';

        // Handle nested player errors
        if (path.startsWith('players[')) {
          const match = path.match(/players\[(\d+)\]\.(.+)/);
          if (match) {
            const index = parseInt(match[1]);
            const field = match[2];

            // Ensure the array has an object at this index
            if (!playerErrors[index]) {
              playerErrors[index] = {};
            }

            playerErrors[index][field] = error.message;
          }
        } else {
          // Handle top-level errors
          errors[path] = error.message;
        }
      });
    }
    return false;
  }
};

// Enhanced debugging for the getPlayerError function
const getPlayerError = (index: number, field: string): string => {
  if (!playerErrors[index]) return '';
  const error = playerErrors[index][field] || '';

  // Debug logging to help understand what's happening
  if (field === 'size' && formData.players[index]) {
    if (error) {
      logError(`Player ${index} size error:`, error);
    }
  }

  return error;
};

// Enhanced onSubmit with better debugging
const onSubmit = async () => {
  isSubmitting.value = true;

  try {
    const isValid = await validateForm();
    if (!isValid) {
      isSubmitting.value = false;
      showAlert('', 'Please fix the validation errors before submitting', 'error');
    }
    if(!isValid){
      return;
    }
    const payload = getPayload(formData);
    emit('add-factory-product', payload);
    showToast('Product added successfully', 'success');
    closeEvent();
  } catch (error: any) {
    logError('Submit error:', error);
    handleResponseException(error);
    showAlert('', 'An error occurred while adding the product', 'error');
  } finally {
    isSubmitting.value = false;
  }
};

const closeEvent = () => {
  emit('onClose');
};

const fileChange = (e: Event, type: string) => {
  const target = e.target as HTMLInputElement;
  if (target.files) {
    if (type === 'assets') {
      formData.productAssets = [...formData.productAssets, ...Array.from(target.files)];
    }
    if (type === 'frontimage') {
      formData.frontImage = target.files[0];
    }
    if (type === 'backimage') {
      formData.backImage = target.files[0];
    }
    if (type === 'roster') {
      if (isCustomProduct.value) {
        rosterFile.value = target.files[0];
        uploadExcelFile(target.files[0]);
      } else {
        uploadExcelFile(target.files[0]);
      }
    }
    (e.target as HTMLInputElement).value = '';
  }
};

const getFilePreview = (file: File | undefined) => {
  if (!file) return placeholderImg;

  const file_ext = file.name.split('.').pop()?.toLowerCase() || '';
  if (file_ext === 'jpg' || file_ext === 'jpeg' || (file_ext === 'png' && file)) {
    return URL.createObjectURL(file);
  } else {
    return placeholderImg;
  }
};

const removeFile = (index: number | null, type: string) => {
  if (type === 'assets' && index !== null) {
    formData.productAssets.splice(index, 1);
  } else if (type === 'frontimage') {
    formData.frontImage = undefined;
  } else if (type === 'backimage') {
    formData.backImage = undefined;
  } else if (type === 'roster') {
    rosterFile.value = undefined;
  }
};

const uploadExcelFile = (file: File) => {
  loading.value = true;
  const ext = file.name.split('.').pop();
  if (ext !== 'xlsx') {
    showAlert('', 'Invalid file format. Please upload an XLSX file.', 'error');
    loading.value = false;
    return;
  }

  readXlsxFile(file).then(async (rows: any[][]) => {
    if (
      rows.length < 2 ||
      rows[0].length !== 4 ||
      rows[0][0] !== 'NAME ON PRODUCT' ||
      rows[0][1] !== 'NUMBER' ||
      rows[0][2] !== 'SIZE*' ||
      rows[0][3] !== 'QUANTITY*'
    ) {
      showAlert('', 'Invalid file structure.', 'error');
      loading.value = false;
      return;
    }

    const updated_roster: Player[] = rows.slice(1).map((row) => ({
      text: row[0] || '',
      number: row[1] ? String(row[1]) : '',
      size: row[2] || '',
      quantity: row[3] || 0,
      information: '',
    }));

    formData.players = updated_roster;
    loading.value = false;
    showToast('Excel file uploaded successfully', 'success');
  });
};

const setRosterData = async (option: LockerRoomProduct): Promise<void> => {
  const rosters = option.product_roster_detail?.map((product) => {
    return {
      size: product.size,
      text: product.text,
      number: product.number,
      quantity: product.quantity,
      information: '',
    };
  });

  if (rosters) {
    formData.players = rosters;
  }
};

const addPlayer = () => {
  let prevPlayer

  if (formData.players.length > 0) {
    prevPlayer = formData.players[formData.players.length - 1];
  }

  formData.players.push({
    text: '',
    number: '',
    size: prevPlayer ? prevPlayer.size : '' ,
    quantity: 1,
    information: '',
  });
};

const removePlayer = (index: number) => {
  formData.players.splice(index, 1);
};

const onSearch = () => {
  searchActive.value = true;
};

const onOptionSelecting = (option: any) => {
  if(option.factory_id){
    formData.selectedFactoryId = props.factories.find(fac => fac.id === option.factory_id);
  }else{
    formData.selectedFactoryId = null;
  }
  formData.selectedProduct = option;
  resetProductFields('selectedProduct');
  searchActive.value = false;
};

const onInput = (val: string) => {
  if (searchActive.value && typeof val === 'string') {
    return;
  }
};

// Reset functions
const resetProductFields = async (fieldName: string) => {
  switch (fieldName) {
    case 'selectedProduct':
      formData.selectedStyle = null;
      formData.selectedDesign = null;
      // Reset player sizes when product changes
      formData.players.forEach((player) => {
        player.size = '';
      });
      break;
    case 'selectedStyle':
      formData.selectedDesign = null;
      break;
  }
};

// Addon selection functions
const setSelectedAddons = (e: any, selected_addon: any, index: number) => {
  if (e.target.checked) {
    selectedAddons.value.push(selected_addon);
  } else {
    selectedAddons.value.splice(index, 1);
  }
};

const setLogoTechnologies = (e: any, logo_technology: any, index: number) => {
  if (e.target.checked) {
    selectedTechnology.value.push(logo_technology);
  } else {
    selectedTechnology.value.splice(index, 1);
  }
};

const setSelectedGroupedAddons = (group: string, selectedAddon: any): void => {
  selectedGroupAddon.value[group] = selectedAddon;
};

// Payload generation function - COMPLETE IMPLEMENTATION
const getPayload = (values: any) => {
  const order_item = props.orderItem as DashboardItem;
  const selectedFactory = props.factories.find(fac => fac.id === values.selectedFactoryId);
  let manual_order_payload = {
    company_id: props.companyId,
    company_name: order_item.order.company_name,
    customer_id: order_item.order.customer_id,
    customer_name: order_item.order.customer_name,
    factory_id: selectedFactory?.id || null,
    factory_name: selectedFactory?.name || null,
    factory_products: [] as any[],
    price_info: {} as any,
  };

  const addons = getValidAddons();
  let factory_product_data = {
    //id will be set on backend
    id: '',
    addons: addons,
    assets: isCustomProduct.value
      ? values.productAssets.map((factory_product_asset: any) => factory_product_asset)
      : [],
    svg_url: '',
    sync_id: null,
    style_id: isCustomProduct.value ? null : values.selectedStyle?.id,
    style_name: isCustomProduct.value ? null : values.selectedStyle?.name,
    design_id: isCustomProduct.value ? -1 : values.selectedDesign?.id,
    design_nick_name: isCustomProduct.value ? values.customDesignName : null,
    svg_parts: [],
    back_image: 'placeholders/custom-design.png',
    front_image: 'placeholders/custom-design.png',
    is_new_product: isCustomProduct.value,
    new_product_name: isCustomProduct.value ? values.customProductName : null,
    product_id: !isCustomProduct.value ? values.selectedProduct?.id : null,
    sku_number: !isCustomProduct.value ? values.selectedProduct?.sku?.sku_number : null,
    product_name: !isCustomProduct.value ? values.selectedProduct?.display_name : null,
    product_type: !isCustomProduct.value ? values.selectedProduct?.product_type : null,
    sort_order: null,
    reorder_data: null,
    grouped_addons: [],
    colors: [],
    svg_groups: [],
    groupcolors: [],
    logo_colors: [],
    defaultcolors: [],
    custom_logo_svgs: [],
    group_patterns: [],
    prices: null,
    production_url: '',
    back_image_short: '',
    fixed_logo_index: 0,
    ungrouped_addons: [],
    front_image_short: '',
    is_custom_product: isCustomProduct.value,
    custom_product_placeholder: 'placeholders/custom-design.png',
    measurement_ratio: !isCustomProduct.value ? values.selectedProduct?.measurement_ratio : null,
    sizechart_reference: !isCustomProduct.value
      ? values.selectedProduct?.sku?.sizechart_reference
      : null,
    shuffle_color_number: 1,
    product_roster_detail: values.players,
    size_variants_mapping: null,
    minimum_order_quantity: !isCustomProduct.value
      ? values.selectedProduct?.sku?.minimum_order_quantity
      : null,
    minimum_order_quantity_type: !isCustomProduct.value
      ? values.selectedProduct?.sku?.minimum_order_quantity_type
      : null,
    product_custom_text_objects: { common: [], roster: [] },
    front_image_file: values.frontImage,
    back_image_file: values.backImage,
    ecommerce_cart_id: null,
    ecommerce_post_id: null,
    ecommerce_variant_id: null,
    ecommerce_modifier_id: null,
    product_custom_texts: [], //need to populate
    custom_logos: [],
  };
   const product_prices = getProductPrices(factory_product_data, values.selectedProduct);
  factory_product_data.prices = product_prices;
    manual_order_payload.price_info = {
    discount: 0,
    is_fixed: false,
    shipping: 0,
    fixed_value: 0,
    total_price: product_prices.factory_product_total_price ?? 0,
    total_quantity: product_prices.total_quantity ?? 0,
    order_item_final_price: product_prices.factory_product_total_price ?? 0,
  };
  manual_order_payload.factory_products.push(factory_product_data);
  return manual_order_payload;
};

// COMPLETE PRICING IMPLEMENTATION
const getProductPrices = (factory_product: any, product: any) => {
  let product_sku_price = {
    is_fixed: false,
    fixed_value: 0,
    discount_applied: 0,
    price_applied_of: 'sku',
    discount_applied_in_percent: 0,
    net_price: 0,
    final_net_price: 0,
  };

  if (product) {
    product_sku_price = product.sku.price;
  }

  let total_quantity: number = 0;
  let product_prices = {
    sizes: [],
    addons: [],
    logo_technologies: [],
    grouped_addons: {},
    ungrouped_addons: [],
    total_addons: 0,
    updated_by_info: {
      id: '',
      name: '',
      updated_at: '',
    },
    total_quantity: 0,
    sizes_discount_info: {
      is_fixed: product_sku_price.is_fixed,
      fixed_value: product_sku_price.fixed_value,
      discount_applied: product_sku_price.discount_applied,
      price_applied_of: product_sku_price.price_applied_of,
      discount_applied_in_percent: product_sku_price.discount_applied_in_percent,
    },
    factory_product_total_price: 0,
  };

  factory_product.product_roster_detail.forEach((rosterData: any) => {
    total_quantity += parseInt(rosterData.quantity);
    let product_net_price = product ? product.sku.price.net_price : 0;
    product_net_price = parseFloat(product_net_price);
    let product_final_net_price = product ? product.sku.price.final_net_price : 0;
    product_final_net_price = parseFloat(product_final_net_price);
    const size_quantity = parseInt(rosterData.quantity);
    const sizeObj = {
      size: rosterData.size,
      quantity: size_quantity,
      original_price: product_net_price,
      final_net_price: product_final_net_price,
      final_net_price_with_quantity: product_final_net_price * size_quantity,
    };
    product_prices.sizes.push(sizeObj);
    product_prices.factory_product_total_price += sizeObj.final_net_price_with_quantity;
  });

  product_prices.total_quantity = total_quantity;

  // Process regular addons
  product_prices.addons = selectedAddons.value.map((addon) => {
    const selected_addon_obj = {
      addon_id: addon.addon_id,
      is_fixed: addon.price_info.is_fixed,
      net_price: parseFloat(addon.price_info.net_price),
      addon_name: addon.title,
      fixed_value: addon.price_info.fixed_value,
      original_price: parseFloat(addon.price_info.net_price),
      final_net_price: addon.price_info.final_net_price,
      discount_applied: addon.price_info.discount_applied,
      price_applied_of: addon.price_info.price_applied_of,
      discount_applied_in_percent: 0,
      final_net_price_with_quantity: addon.price_info.net_price
        ? parseFloat(addon.price_info.net_price) * total_quantity
        : 0,
    };
    product_prices.factory_product_total_price += selected_addon_obj.final_net_price_with_quantity;
    return selected_addon_obj;
  });

  // Process logo technologies
  product_prices.logo_technologies = selectedTechnology.value.map((addon) => {
    const selected_addon_obj = {
      logo_id: '',
      side: '',
      logo_url: '',
      addon_id: addon.sku_id,
      is_fixed: addon.price_info.is_fixed,
      net_price: parseFloat(addon.price_info.net_price),
      addon_name: addon.label,
      fixed_value: addon.price_info.fixed_value,
      original_price: parseFloat(addon.price_info.net_price),
      final_net_price: addon.price_info.final_net_price,
      discount_applied: addon.price_info.discount_applied,
      price_applied_of: addon.price_info.price_applied_of,
      discount_applied_in_percent: 0,
      final_net_price_with_quantity: addon.price_info.net_price
        ? parseFloat(addon.price_info.net_price) * total_quantity
        : 0,
    };
    product_prices.factory_product_total_price += selected_addon_obj.final_net_price_with_quantity;
    return selected_addon_obj;
  });

  // Process grouped addons
  if (Object.keys(selectedGroupAddon.value).length > 0) {
    product_prices.grouped_addons = Object.fromEntries(
      Object.entries(selectedGroupAddon.value).map(([group, addon]) => {
        const price_info = addon.price_info;
        const net_price = price_info?.net_price ? parseFloat(price_info.net_price) : 0;
        let final_net_price_with_quantity = net_price * total_quantity;
        product_prices.factory_product_total_price += final_net_price_with_quantity;
        return [
          group,
          {
            addon_id: addon.id,
            is_fixed: price_info?.is_fixed || false,
            net_price: net_price,
            addon_name: addon.sku_id,
            fixed_value: price_info?.fixed_value || 0,
            original_price: net_price,
            final_net_price: price_info?.final_net_price || 0,
            discount_applied: price_info?.discount_applied || 0,
            price_applied_of: price_info?.price_applied_of || 'sku',
            discount_applied_in_percent: price_info?.discount_applied_in_percent || 0,
            final_net_price_with_quantity: final_net_price_with_quantity,
          },
        ];
      })
    );
  } else {
    product_prices.grouped_addons = {}; // Reset if no selections
  }

  // Process ungrouped addons
  if (formData.selectedStyle) {
    product_prices.ungrouped_addons = formData.selectedStyle?.customized_addons.ungrouped_addons
      .filter((addon) => addon.is_selected)
      .map((addon) => {
        const selected_ungrouped_addon = {
          addon_id: addon.addon_id,
          is_fixed: addon.price_info.is_fixed,
          net_price: addon.price_info.net_price ? parseFloat(addon.price_info.net_price) : 0,
          addon_name: addon.sku_id,
          fixed_value: addon.price_info.fixed_value,
          original_price: addon.price_info.net_price ? parseFloat(addon.price_info.net_price) : 0,
          final_net_price: addon.price_info.final_net_price,
          discount_applied: addon.price_info.discount_applied,
          price_applied_of: addon.price_info.price_applied_of,
          discount_applied_in_percent: addon.price_info.discount_applied_in_percent,
          final_net_price_with_quantity: addon.price_info.net_price
            ? parseFloat(addon.price_info.net_price) * total_quantity
            : 0,
        };
        product_prices.factory_product_total_price +=
          selected_ungrouped_addon.final_net_price_with_quantity;
        return selected_ungrouped_addon;
      });
  }

  product_prices.total_addons =
    product_prices.addons.length +
    product_prices.ungrouped_addons.length +
    product_prices.logo_technologies.length +
    Object.keys(product_prices.grouped_addons).length;

  return product_prices;
};

const getValidAddons = (): any[] => {
  const validAddons = selectedAddons.value.map((addon: any): any => {
    if (addon && typeof addon === 'object' && 'title' in addon) {
      return addon;
    } else if (typeof addon === 'string') {
      return { title: addon };
    } else {
      return null;
    }
  });
  return validAddons.filter((addon) => addon !== null);
};

const copyRoster = (rosterData :any[]) => {
  let productId = !isCustomProduct.value ? formData.selectedProduct?.id : formData.customProductName;
  emit('copy-roster', {'product_id': productId,'rosterData': rosterData});
}

const pasteRoster = () => {
  let rosterData = [];
  if(props.copiedRoster && props.copiedRoster.rosterData){
    props.copiedRoster.rosterData.forEach((player) => {
      let matchedSize = '';
      if (!isCustomProduct.value && formData.selectedProduct) {
        formData.selectedProduct.sizes[0].json_data.forEach((size) => {
          if(size.name === player.size){
            matchedSize = size.name;
          }
        });
      } else {
        matchedSize = player.size;
      }
      rosterData.push({
        text: player.text,
        number: player.number,
        size: matchedSize,
        quantity: player.quantity,
        information: '',
      });
    });
    formData.players = rosterData;
  }
}

// Lifecycle hooks
onMounted(async () => {
  // await getMerchantProducts(props.companyId);
  // await getCustomerLockerRoom(props.companyId, props.customerId);
  // await getFactories()
});

watch(isCustomProduct, () => {
  // Reset form when switching between custom and standard product
  formData.customProductName = null;
  formData.customDesignName = null;
  formData.productAssets = [];
  formData.frontImage = undefined;
  formData.backImage = undefined;
  formData.selectedProduct = null;
  formData.selectedStyle = null;
  formData.selectedDesign = null;
  formData.selectedFactoryId = null;

  // Clear errors
  Object.keys(errors).forEach((key) => delete errors[key]);
  playerErrors.splice(0, playerErrors.length);

  // Reset addon selections
  selectedAddons.value = [];
  selectedTechnology.value = [];
  selectedGroupAddon.value = {};
});

// Watch for selectedProduct changes to update selectedStyle and selectedDesign
watch(
  () => formData.selectedProduct,
  (newProduct) => {
    if (newProduct) {
      formData.selectedStyle = null;
      formData.selectedDesign = null;
    }
  }
);

// Watch for selectedStyle changes to update selectedDesign
watch(
  () => formData.selectedStyle,
  (newStyle) => {
    if (newStyle) {
      formData.selectedDesign = null;
    }
  }
);
</script>

<style lang="scss" scoped>
label {
  &:has(input:checked) {
    @apply bg-primary text-white;

    .checkIcon {
      display: block;
    }
  }
}
</style>
